import { Injectable } from '@angular/core';
import { DrawingData, DrawingLevelwiseData, LaborData, LaborLevelwiseData, MaterialData, MaterialLevelwiseData, MiscData, MiscLevelwiseData } from '../../models/dtos';


/**
 * IndexedDB Service for storing and retrieving levelwise data
 * Provides efficient querying based on GUID and parent GUID
 * Each project gets its own database instance
 */
type StoreType = 'material' | 'labor' | 'misc' | 'drawing';
@Injectable({
  providedIn: 'root'
})

export class IndexDBService {
  private readonly DB_NAME_PREFIX = 'SiQuoteLevelwiseData';
  private readonly DB_VERSION = 1;

  /**
   * localStorage key for tracking IndexedDB databases pending cleanup.
   * When a database is created, its GUID is added to this list.
   * When a database is successfully deleted, its GUID is removed.
   * Anything left in this list on startup = orphaned (deletion didn't complete).
   * Used by the mark-and-sweep cleanup strategy.
   */
  private readonly PENDING_CLEANUP_STORAGE_KEY = 'sqweb_indexeddb_pending_cleanup';

  /**
   * localStorage key for tracking DBs with corrupt/incomplete schema.
   * DBs that fail repair attempts are marked here for force-removal.
   * Used to distinguish between "blocked deletion" and "genuinely corrupt DB".
   */
  private readonly CORRUPT_SCHEMA_STORAGE_KEY = 'sqweb_indexeddb_corrupt_schema';

  // Object store names
  private readonly MATERIAL_STORE = 'materialData';
  private readonly LABOR_STORE = 'laborData';
  private readonly MISC_STORE = 'miscData';
  private readonly DRAWING_STORE = 'drawingData';

  private db: IDBDatabase | null = null;
  private openDbPromise: Promise<void> | null = null;
  private openDbPromiseDbName: string | null = null;
  private currentProjectGuid: string | null = null;
  private currentDbName: string | null = null;

  constructor() {}
  /**
   * Set the project GUID and initialize/switch to the project-specific database
   */
  async setProjectGuid(projectGuid: string): Promise<void> {
    // Close current database if switching projects
    if (this.currentProjectGuid !== projectGuid && this.db) {
      this.closeDatabase();
    }

    this.currentProjectGuid = projectGuid;
    this.currentDbName = `${this.DB_NAME_PREFIX}_${projectGuid}`;
    
    await this.initDatabase();
  }

  /**
   * Get the current project GUID
   */
  getCurrentProjectGuid(): string | null {
    return this.currentProjectGuid;
  }

  /**
   * Returns true when a project database has been initialised.
   * Use this to guard read operations when no estimate is open.
   */
  isDatabaseReady(): boolean {
    return this.currentDbName !== null;
  }

  /**
   * Initialize IndexedDB with proper schema and indexes
   */
  private async initDatabase(): Promise<void> {
    if (!this.currentDbName) {
      throw new Error('Project GUID must be set before initializing database. Call setProjectGuid() first.');
    }

    if (!this.currentProjectGuid) {
      throw new Error('Project GUID must be set before initializing database.');
    }

    const targetDbName = this.currentDbName;
    const targetProjectGuid = this.currentProjectGuid;

    if (this.db && this.db.name === targetDbName && this.hasRequiredStores(this.db)) {
      return;
    }

    if (this.openDbPromise && this.openDbPromiseDbName === targetDbName) {
      return this.openDbPromise;
    }

    const openPromise = this.openDatabaseAndEnsureSchema(targetDbName, targetProjectGuid)
      .finally(() => {
        if (this.openDbPromise === openPromise) {
          this.openDbPromise = null;
          this.openDbPromiseDbName = null;
        }
      });

    this.openDbPromise = openPromise;
    this.openDbPromiseDbName = targetDbName;

    return openPromise;
  }

  /**
   * Open IndexedDB and guarantee required stores exist.
   * On first open (onupgradeneeded), creates all required stores.
   * On subsequent opens, validates that stores are present.
   */
  private async openDatabaseAndEnsureSchema(targetDbName: string, targetProjectGuid: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(targetDbName, this.DB_VERSION);

      request.onerror = () => {
        console.error(`IndexedDB failed to open:`, request.error);
        reject(request.error);
      };

      request.onblocked = () => {
        const msg = `IndexedDB open blocked for ${targetDbName}. Another tab/connection is holding it.`;
        console.warn(msg);
        reject(new Error(msg));
      };

      request.onsuccess = () => {
        const openedDb = request.result;

        // If project switched while opening, do not bind stale connection.
        if (this.currentDbName !== targetDbName) {
          openedDb.close();
          resolve();
          return;
        }

        openedDb.onversionchange = () => {
          console.warn('IndexedDB version changed elsewhere. Closing local connection.');
          if (this.db === openedDb) {
            this.closeDatabase();
          } else {
            openedDb.close();
          }
        };

        this.db = openedDb;

        if (this.hasRequiredStores(openedDb)) {
          resolve();
          return;
        }

        // Schema is incomplete — this means either onupgradeneeded failed
        // or the DB is corrupted from a previous session
        const msg = `IndexedDB schema incomplete for project ${targetProjectGuid}. Required stores are missing.`;
        console.error(msg);
        this.markCorruptSchema(targetProjectGuid);
        openedDb.close();
        if (this.db === openedDb) {
          this.db = null;
        }
        reject(new Error(msg));
      };

      request.onupgradeneeded = (event) => {
        const openRequest = event.target as IDBOpenDBRequest;
        const db = openRequest.result;
        const transaction = openRequest.transaction;

        if (!transaction) {
          reject(new Error('IndexedDB upgrade transaction was not created.'));
          return;
        }
        
        // Create object stores with indexes
        this.ensureObjectStoreSchema(db, transaction, this.MATERIAL_STORE);
        this.ensureObjectStoreSchema(db, transaction, this.LABOR_STORE);
        this.ensureObjectStoreSchema(db, transaction, this.MISC_STORE);
        this.ensureObjectStoreSchema(db, transaction, this.DRAWING_STORE);
      };
    });
  }

  /**
   * Verify all required stores are present in the opened database.
   */
  private hasRequiredStores(db: IDBDatabase): boolean {
    return this.getRequiredStoreNames().every((storeName) => db.objectStoreNames.contains(storeName));
  }

  private getRequiredStoreNames(): string[] {
    return [this.MATERIAL_STORE, this.LABOR_STORE, this.MISC_STORE, this.DRAWING_STORE];
  }

  /**
   * Create or update an object store with GUID, parent GUID, and nodeType indexes.
   */
  private ensureObjectStoreSchema(db: IDBDatabase, transaction: IDBTransaction, storeName: string): void {
    const objectStore = db.objectStoreNames.contains(storeName)
      ? transaction.objectStore(storeName)
      : db.createObjectStore(storeName, { keyPath: 'guid' });

    if (!objectStore.indexNames.contains('parentGuid')) {
      objectStore.createIndex('parentGuid', 'parentGuid', { unique: false });
    }

    if (!objectStore.indexNames.contains('guid')) {
      objectStore.createIndex('guid', 'guid', { unique: true });
    }

    if (!objectStore.indexNames.contains('nodeType')) {
      objectStore.createIndex('nodeType', 'nodeType', { unique: false });
    }

  }

  /**
   * Ensure database is ready before operations
   */
  private async ensureDbReady(): Promise<IDBDatabase> {
    if (!this.currentDbName) {
      throw new Error('Project GUID must be set before database operations.');
    }

    const isWrongDatabase = !!this.db && this.db.name !== this.currentDbName;
    const hasIncompleteSchema = !!this.db && !this.hasRequiredStores(this.db);

    if (!this.db || isWrongDatabase || hasIncompleteSchema) {
      if (isWrongDatabase || hasIncompleteSchema) {
        const reason = isWrongDatabase
          ? `IndexedDB connection is for ${this.db!.name}, expected ${this.currentDbName}. Reinitializing.`
          : 'IndexedDB connection has incomplete schema. Reinitializing.';
        console.warn(reason);
        this.closeDatabase();
      }
      await this.initDatabase();
    }
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    return this.db;
  }

  // ========== MATERIAL DATA OPERATIONS ==========

  /**
   * Store material levelwise data
   */
  async storeMaterialData(data: MaterialLevelwiseData): Promise<void> {
    const db = await this.ensureDbReady();
    
    const entries: Array<{ guid: string; parentGuid: string; nodeType: string; data: MaterialData }> = [];
    for (const [guid, materialData] of Object.entries(data.materialsCollection || {})) {
      entries.push({ 
        guid,
        parentGuid: materialData.itemParentGuid || '',
        nodeType: materialData.nodeType || '',
        data: materialData 
      });
    }
    
    const transaction = db.transaction([this.MATERIAL_STORE], 'readwrite');
    const store = transaction.objectStore(this.MATERIAL_STORE);

    const promises: Promise<void>[] = [];
    for (const entry of entries) {
      promises.push(this.putData(store, entry));
    }

    await Promise.all(promises);
    return this.waitForTransaction(transaction);
  }

  /**
   * Get material data by GUID
   */
  async getMaterialDataByGuid(guid: string): Promise<MaterialData | null> {
    if (!this.isDatabaseReady()) {
      return null;
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.MATERIAL_STORE], 'readonly');
    const store = transaction.objectStore(this.MATERIAL_STORE);
    const index = store.index('guid');

    return this.getData<MaterialData>(index, guid);
  }

  /**
   * Get all material data for children of a parent GUID
   */
  async getMaterialDataByParentGuid(parentGuid: string): Promise<Record<string, MaterialData>> {
    if (!this.isDatabaseReady()) {
      return {};
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.MATERIAL_STORE], 'readonly');
    const store = transaction.objectStore(this.MATERIAL_STORE);
    const index = store.index('parentGuid');
    return this.getDataByIndex<MaterialData>(index, parentGuid);
  }

  /**
   * Get all material data by nodeType
   */
  async getMaterialDataByNodeType(nodeType: string): Promise<Record<string, MaterialData>> {
    if (!this.isDatabaseReady()) {
      return {};
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.MATERIAL_STORE], 'readonly');
    const store = transaction.objectStore(this.MATERIAL_STORE);
    const index = store.index('nodeType');
    return this.getDataByIndex<MaterialData>(index, nodeType);
  }

  /**
   * Get all material data
   */
  async getAllMaterialData(): Promise<MaterialLevelwiseData> {
    if (!this.isDatabaseReady()) {
      return { materialsCollection: {} };
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.MATERIAL_STORE], 'readonly');
    const store = transaction.objectStore(this.MATERIAL_STORE);
    return this.getAllData<MaterialData>(store, 'materialsCollection');
  }  
  
  // ========== LABOR DATA OPERATIONS ==========

  /**
   * Store labor levelwise data
   */
  async storeLaborData(data: LaborLevelwiseData): Promise<void> {
    const db = await this.ensureDbReady();
    
    const entries: Array<{ guid: string; parentGuid: string; nodeType: string; data: LaborData }> = [];
    for (const [guid, laborData] of Object.entries(data.laborsCollection || {})) {
      entries.push({ 
        guid,
        parentGuid: laborData.itemParentGuid || '',
        nodeType: laborData.nodeType || '',
        data: laborData 
      });
    }
    
    const transaction = db.transaction([this.LABOR_STORE], 'readwrite');
    const store = transaction.objectStore(this.LABOR_STORE);

    const promises: Promise<void>[] = [];
    for (const entry of entries) {
      promises.push(this.putData(store, entry));
    }

    await Promise.all(promises);
    return this.waitForTransaction(transaction);
  }

  /**
   * Get labor data by GUID
   */
  async getLaborDataByGuid(guid: string): Promise<LaborData | null> {
    if (!this.isDatabaseReady()) {
      return null;
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.LABOR_STORE], 'readonly');
    const store = transaction.objectStore(this.LABOR_STORE);
    const index = store.index('guid');

    return this.getData<LaborData>(index, guid);
  }

  /**
   * Get all labor data for children of a parent GUID
   */
  async getLaborDataByParentGuid(parentGuid: string): Promise<Record<string, LaborData>> {
    if (!this.isDatabaseReady()) {
      return {};
    }   
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.LABOR_STORE], 'readonly');
    const store = transaction.objectStore(this.LABOR_STORE);
    const index = store.index('parentGuid');
    return this.getDataByIndex<LaborData>(index, parentGuid);
  }

  /**
   * Get all labor data by nodeType
   */
  async getLaborDataByNodeType(nodeType: string): Promise<Record<string, LaborData>> {
    if (!this.isDatabaseReady()) {
      return {};
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.LABOR_STORE], 'readonly');
    const store = transaction.objectStore(this.LABOR_STORE);
    const index = store.index('nodeType');
    return this.getDataByIndex<LaborData>(index, nodeType);
  }

  /**
   * Get all labor data
   */
  async getAllLaborData(): Promise<LaborLevelwiseData> {
    if (!this.isDatabaseReady()) {
      return { laborsCollection: {} };
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.LABOR_STORE], 'readonly');
    const store = transaction.objectStore(this.LABOR_STORE);
    return this.getAllData<LaborData>(store, 'laborsCollection');
  }
 
  // ========== MISC DATA OPERATIONS ==========

  /**
   * Store misc levelwise data
   */
  async storeMiscData(data: MiscLevelwiseData): Promise<void> {
    const db = await this.ensureDbReady();
    
    const entries: Array<{ guid: string; parentGuid: string; nodeType: string; data: MiscData }> = [];
    for (const [guid, miscData] of Object.entries(data.miscsCollection || {})) {
      entries.push({ 
        guid,
        parentGuid: miscData.itemParentGuid || '',
        nodeType: miscData.nodeType || '',
        data: miscData 
      });
    }
    
    const transaction = db.transaction([this.MISC_STORE], 'readwrite');
    const store = transaction.objectStore(this.MISC_STORE);

    const promises: Promise<void>[] = [];
    for (const entry of entries) {
      promises.push(this.putData(store, entry));
    }

    await Promise.all(promises);
    return this.waitForTransaction(transaction);
  }

  /**
   * Get misc data by GUID
   */
  async getMiscDataByGuid(guid: string): Promise<MiscData | null> {
    if (!this.isDatabaseReady()) {
      return null;
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.MISC_STORE], 'readonly');
    const store = transaction.objectStore(this.MISC_STORE);
    const index = store.index('guid');

    return this.getData<MiscData>(index, guid);
  }

  /**
   * Get all misc data for children of a parent GUID
   */
  async getMiscDataByParentGuid(parentGuid: string): Promise<Record<string, MiscData>> {
    if (!this.isDatabaseReady()) {
      return {};
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.MISC_STORE], 'readonly');
    const store = transaction.objectStore(this.MISC_STORE);
    const index = store.index('parentGuid');
    return this.getDataByIndex<MiscData>(index, parentGuid);
  }

  /**
   * Get all misc data by nodeType
   */
  async getMiscDataByNodeType(nodeType: string): Promise<Record<string, MiscData>> {
    if (!this.isDatabaseReady()) {
      return {};
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.MISC_STORE], 'readonly');
    const store = transaction.objectStore(this.MISC_STORE);
    const index = store.index('nodeType');
    return this.getDataByIndex<MiscData>(index, nodeType);
  }

  /**
   * Get all misc data
   */
  async getAllMiscData(): Promise<MiscLevelwiseData> {
    if (!this.isDatabaseReady()) {
      return { miscsCollection: {} };
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.MISC_STORE], 'readonly');
    const store = transaction.objectStore(this.MISC_STORE);
    return this.getAllData<MiscData>(store, 'miscsCollection');
  }
  
  // ========== DRAWING DATA OPERATIONS ==========

  /**
   * Store drawing levelwise data
   */
  async storeDrawingData(data: DrawingLevelwiseData): Promise<void> {
    const db = await this.ensureDbReady();
    
    const entries: Array<{ guid: string; parentGuid: string; nodeType: string; data: DrawingData }> = [];
    for (const [guid, drawingData] of Object.entries(data.drawingDataCollection || {})) {
      entries.push({ 
        guid,
        parentGuid: drawingData.itemParentGuid || '',
        nodeType: drawingData.nodeType || '',
        data: drawingData 
      });
    }
    
    const transaction = db.transaction([this.DRAWING_STORE], 'readwrite');
    const store = transaction.objectStore(this.DRAWING_STORE);

    const promises: Promise<void>[] = [];
    for (const entry of entries) {
      promises.push(this.putData(store, entry));
    }

    await Promise.all(promises);
    return this.waitForTransaction(transaction);
  }

  /**
   * Get drawing data by GUID
   */
  async getDrawingDataByGuid(guid: string): Promise<DrawingData | null> {
    if (!this.isDatabaseReady()) {
      return null;
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.DRAWING_STORE], 'readonly');
    const store = transaction.objectStore(this.DRAWING_STORE);
    const index = store.index('guid');

    return this.getData<DrawingData>(index, guid);
  }
  /**
   * Get all drawing data for children of a parent GUID
   */
  async getDrawingDataByParentGuid(parentGuid: string): Promise<Record<string, DrawingData>> {
    if (!this.isDatabaseReady()) {
      return {};
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.DRAWING_STORE], 'readonly');
    const store = transaction.objectStore(this.DRAWING_STORE);
    const index = store.index('parentGuid');
    return this.getDataByIndex<DrawingData>(index, parentGuid);
  }

  /**
   * Get all drawing data by nodeType
   */
  async getDrawingDataByNodeType(nodeType: string): Promise<Record<string, DrawingData>> {
    if (!this.isDatabaseReady()) {
      return {};
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.DRAWING_STORE], 'readonly');
    const store = transaction.objectStore(this.DRAWING_STORE);
    const index = store.index('nodeType');
    return this.getDataByIndex<DrawingData>(index, nodeType);
  }

  /**
   * Get all drawing data
   */
  async getAllDrawingData(): Promise<DrawingLevelwiseData> {
    if (!this.isDatabaseReady()) {
      return { drawingDataCollection: {} };
    }
    const db = await this.ensureDbReady();
    const transaction = db.transaction([this.DRAWING_STORE], 'readonly');
    const store = transaction.objectStore(this.DRAWING_STORE);
    return this.getAllData<DrawingData>(store, 'drawingDataCollection');
  }

  // ========== BULK OPERATIONS ==========

  /**
   * Store all levelwise data at once
   */
  async storeAllLevelwiseData(
    materialData: MaterialLevelwiseData,
    laborData: LaborLevelwiseData,
    miscData: MiscLevelwiseData,
    drawingData: DrawingLevelwiseData
  ): Promise<void> {
    await Promise.all([
      this.storeMaterialData(materialData),
      this.storeLaborData(laborData),
      this.storeMiscData(miscData),
      this.storeDrawingData(drawingData)
    ]);
  }

  /**
   * Clear all data from a specific store
   */

 
  async clearStore(storeType: StoreType): Promise<void> {
    const db = await this.ensureDbReady();
    const storeName = this.getStoreName(storeType);
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all data from all stores
   */
  async clearAllStores(): Promise<void> {
    await Promise.all([
      this.clearStore('material'),
      this.clearStore('labor'),
      this.clearStore('misc'),
      this.clearStore('drawing')
    ]);
  }

  /**
   * Delete specific entries by GUID
   */
  async deleteByGuid(storeType: StoreType, guid: string): Promise<void> {
    const db = await this.ensureDbReady();
    const storeName = this.getStoreName(storeType);
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.delete(guid);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete all entries with a specific parent GUID
   * Be causious when deleting using with GUID as it will delete all child entries.
   * And sending project GUID will delete entire store data.
   */
  async deleteByParentGuid(storeType: StoreType, parentGuid: string): Promise<void> {
    const db = await this.ensureDbReady();
    const storeName = this.getStoreName(storeType);
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const index = store.index('parentGuid');
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.only(parentGuid));
      
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }
 
  // ========== HELPER METHODS ==========

  /**
   * Get store name from type
   */
  private getStoreName(storeType: StoreType): string {
    const storeMap = {
      material: this.MATERIAL_STORE,
      labor: this.LABOR_STORE,
      misc: this.MISC_STORE,
      drawing: this.DRAWING_STORE
    };
    return storeMap[storeType];
  }

  /**
   * Put data into store
   */
  private putData(store: IDBObjectStore, data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
  /**
   * Get single data item from index
   */
  private getData<T>(index: IDBIndex, key: string): Promise<T | null> {
    return new Promise((resolve, reject) => {
      const request = index.get(key);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data as T : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get multiple data items by index
   */
  private getDataByIndex<T>(index: IDBIndex, key: string): Promise<Record<string, T>> {
    return new Promise((resolve, reject) => {
      const result: Record<string, T> = {};
      const request = index.openCursor(IDBKeyRange.only(key));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          result[cursor.value.guid] = cursor.value.data as T;
          cursor.continue();
        } else {
          resolve(result);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all data from a store
   */
  private getAllData<T>(store: IDBObjectStore, collectionKey: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const result: Record<string, T> = {};
      const request = store.openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          result[cursor.value.guid] = cursor.value.data as T;
          cursor.continue();
        } else {
          const output: any = {};
          output[collectionKey] = result;
          resolve(output);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Wait for transaction to complete
   */
  private waitForTransaction(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(new Error('Transaction aborted'));
    });
  }

  /**
   * Close the database connection
   */
  closeDatabase(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      console.log('IndexedDB closed');
    }
  }
  /**
   * Delete the entire database for the current project
   */
  async deleteDatabase(): Promise<void> {
    if (!this.currentDbName) {
      throw new Error('No database to delete. Project GUID not set.');
    }

    const dbName = this.currentDbName;
    const deletingProjectGuid = this.currentProjectGuid;

    this.closeDatabase();

    await this.deleteDatabaseByName(dbName);

    // Deletion succeeded — remove from pending-cleanup if it was queued
    if (deletingProjectGuid) {
      this.removePendingCleanup(deletingProjectGuid);
    }
    this.currentProjectGuid = null;
    this.currentDbName = null;
  }

  /**
   * Delete a database for a specific project GUID
   */
  async deleteProjectDatabase(projectGuid: string): Promise<void> {
    const dbName = `${this.DB_NAME_PREFIX}_${projectGuid}`;

    if (this.currentProjectGuid === projectGuid) {
      this.closeDatabase();
    }

    await this.deleteDatabaseByName(dbName);

    // Deletion succeeded — remove from pending-cleanup if it was queued
    this.removePendingCleanup(projectGuid);
    if (this.currentProjectGuid === projectGuid) {
      this.currentProjectGuid = null;
      this.currentDbName = null;
      this.db = null;
    }
  }

  /**
   * Delete IndexedDB by name and fail deterministically if blocked for too long.
   * Implements exponential backoff retry on blocked deletion.
   */
  private async deleteDatabaseByName(dbName: string, timeoutMs = 5000, retryCount = 0, maxRetries = 2): Promise<void> {
    const maxRetriesExceeded = retryCount >= maxRetries;
    
    try {
      await this.performDeletion(dbName, timeoutMs);
    } catch (error) {
      const isBlockedError = (error as Error)?.message?.includes('blocked');
      
      if (isBlockedError && retryCount < maxRetries) {
        // Retry with exponential backoff: 1s, 2s, 4s
        const backoffMs = Math.pow(2, retryCount) * 1000;
        console.warn(`Delete blocked for ${dbName}. Retrying in ${backoffMs}ms (attempt ${retryCount + 1}/${maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, backoffMs));
        return this.deleteDatabaseByName(dbName, timeoutMs, retryCount + 1, maxRetries);
      }

      // If we've exhausted retries and it's still blocked, mark the DB as stuck
      if (isBlockedError && maxRetriesExceeded) {
        const extractedGuid = dbName.replace(`${this.DB_NAME_PREFIX}_`, '');
        console.error(`Delete blocked for ${dbName} after ${maxRetries} retries. Marking as stuck for force-removal.`);
        const pending = this.getPendingCleanupList();
        if (!pending.includes(extractedGuid)) {
          pending.push(extractedGuid);
          localStorage.setItem(this.PENDING_CLEANUP_STORAGE_KEY, JSON.stringify(pending));
        }
      }

      throw error;
    }
  }

  /**
   * Core deletion logic with timeout.
   */
  private performDeletion(dbName: string, timeoutMs: number): Promise<void> {
    return new Promise((resolve, reject) => {
      let settled = false;
      let wasBlocked = false;

      const finish = (error?: Error) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timeoutHandle);
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      };

      const request = indexedDB.deleteDatabase(dbName);
      const timeoutHandle = setTimeout(() => {
        const reason = wasBlocked
          ? `IndexedDB deletion timed out (blocked): ${dbName}. Close other tabs/windows using this estimate and retry.`
          : `IndexedDB deletion timed out: ${dbName}.`;
        finish(new Error(reason));
      }, timeoutMs);

      request.onsuccess = () => {
        finish();
      };
      
      request.onerror = () => {
        const errorMsg = request.error?.message || String(request.error);
        console.error(`Delete error for ${dbName}: ${errorMsg}`);
        finish(request.error ?? new Error(`IndexedDB deletion failed: ${dbName}`));
      };
      
      request.onblocked = () => {
        wasBlocked = true;
        console.warn(`Database deletion blocked for ${dbName}. Waiting up to ${timeoutMs}ms before failing...`);
      };
    });
  }

  /**
   * Check if an IDBDatabaseInfo entry belongs to a SiQuote project database.
   * Used as a type guard to ensure the database has a valid name starting with the expected prefix.
   */
  private isSiQuoteDatabase(db: IDBDatabaseInfo): db is IDBDatabaseInfo & { name: string } {
    return !!db?.name?.startsWith(this.DB_NAME_PREFIX);
  }

  /**
   * List all project databases
   */
  async listProjectDatabases(): Promise<string[]> {
    const databases = await indexedDB.databases();
    return databases
      .filter(db => this.isSiQuoteDatabase(db))
      .map(db => db.name.replace(`${this.DB_NAME_PREFIX}_`, ''));
  }

  //#region  cleanup mechanism for orphaned databases

  /**
   * Mark a DB as having corrupt/incomplete schema.
   * Used to distinguish between blocked deletion and genuinely corrupt DBs.
   */
  private markCorruptSchema(projectGuid: string): void {
    const corrupt = this.getCorruptSchemaList();
    if (!corrupt.includes(projectGuid)) {
      corrupt.push(projectGuid);
      localStorage.setItem(this.CORRUPT_SCHEMA_STORAGE_KEY, JSON.stringify(corrupt));
      console.error(`Marked corrupt schema for project: ${projectGuid}`);
    }
  }

  /**
   * Get list of projects with corrupt schemas.
   */
  private getCorruptSchemaList(): string[] {
    try {
      const stored = localStorage.getItem(this.CORRUPT_SCHEMA_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * Remove a project GUID from corrupt schema list.
   */
  private removeCorruptSchema(projectGuid: string): void {
    const corrupt = this.getCorruptSchemaList().filter(guid => guid !== projectGuid);
    if (corrupt.length > 0) {
      localStorage.setItem(this.CORRUPT_SCHEMA_STORAGE_KEY, JSON.stringify(corrupt));
    } else {
      localStorage.removeItem(this.CORRUPT_SCHEMA_STORAGE_KEY);
    }
  }

  /**
   * Get diagnostic summary of all stuck/corrupt databases.
   * Useful for debugging and monitoring.
   */
  async getDiagnosticSummary(): Promise<{
    pendingCleanup: string[];
    corruptSchema: string[];
    existingDbs: string[];
  }> {
    const pendingCleanup = this.getPendingCleanupList();
    const corruptSchema = this.getCorruptSchemaList();
    let existingDbs: string[] = [];
    
    try {
      const databases = await indexedDB.databases();
      existingDbs = databases
        .filter(db => this.isSiQuoteDatabase(db))
        .map(db => db.name!.replace(`${this.DB_NAME_PREFIX}_`, ''));
    } catch (error) {
      console.error('Failed to list databases:', error);
    }

    return { pendingCleanup, corruptSchema, existingDbs };
  }

  /**
   * Queue the current database for cleanup in localStorage.
   * 
   * Called synchronously during pagehide, BEFORE the async deleteDatabase() fires.
   * - If deleteDatabase() completes → removePendingCleanup() clears this entry.
   * - If the browser kills the page first → entry survives in localStorage
   *   → the startup sweep deletes it on next load.
   * 
   * This is the ONLY place a GUID is added to the pending-cleanup list.
   */
  markCurrentDatabaseForDeletion(): void {
    if (!this.currentProjectGuid) {
      return;
    }
    const pending = this.getPendingCleanupList();
    if (!pending.includes(this.currentProjectGuid)) {
      pending.push(this.currentProjectGuid);
      localStorage.setItem(this.PENDING_CLEANUP_STORAGE_KEY, JSON.stringify(pending));
    }
    
    //ToDo : remove this console log after testing, as it may cause performance issue when user have multiple databases and frequently open and close the application.
    console.log(`Database queued for cleanup: ${this.currentProjectGuid}`);
  }

  /**
   * Remove a GUID from the pending-cleanup list.
   * Called after a successful IndexedDB deletion confirms the DB is gone.
   */
  private removePendingCleanup(projectGuid: string): void {
    const pending = this.getPendingCleanupList().filter(guid => guid !== projectGuid);
    if (pending.length > 0) {
      localStorage.setItem(this.PENDING_CLEANUP_STORAGE_KEY, JSON.stringify(pending));
    } else {
      localStorage.removeItem(this.PENDING_CLEANUP_STORAGE_KEY);
    }
  }

  /**
   * Read the pending-cleanup list from localStorage.
   */
  private getPendingCleanupList(): string[] {
    try {
      const stored = localStorage.getItem(this.PENDING_CLEANUP_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * Sweep: Clean up orphaned databases from previous sessions.
   * 
   * How it works:
   * 1. Read the pending-cleanup list (GUIDs whose deletion may not have finished).
   * 2. For each GUID, check if the IndexedDB actually still exists.
   *    - If it exists → delete it, then remove from list.
   *    - If it doesn't exist (deletion already completed) → just remove from list.
   * 
   * Called once during app startup via provideAppInitializer (non-blocking).
   * Uses Web Locks API to prevent concurrent sweeps across multiple tabs.
   */
  async sweepOrphanedDatabases(): Promise<void> {
    try {
      // Guard: use Web Locks API to ensure only one tab sweeps at a time.
      // If the lock is already held by another tab, skip this sweep entirely
      // (the other tab will handle the cleanup).
      if (navigator.locks) {
        const lockGranted = await navigator.locks.request(
          'sqweb_indexeddb_sweep_lock',
          { ifAvailable: true },
          async (lock) => {
            if (!lock) {
              console.log('Sweep: Another tab is already sweeping. Skipping.');
              return false;
            }
            await this.performSweep();
            return true;
          }
        );
        if (!lockGranted) return;
      } else {
        // Fallback: browser doesn't support Web Locks — sweep without guard
        await this.performSweep();
      }
    } catch (error) {
      // Swallow errors so the fire-and-forget call in provideAppInitializer
      // never produces an unhandled promise rejection.
      console.error('Sweep: Unexpected error during orphaned DB cleanup:', error);
    }
  }

  /**
   * Core sweep logic — extracted so it can be called directly or under a Web Lock.
   * Also handles corrupt/incomplete schema databases.
   */
  private async performSweep(): Promise<void> {
    const pending = this.getPendingCleanupList();
    const corrupt = this.getCorruptSchemaList();
    const allToClean = [...new Set([...pending, ...corrupt])];

    if (allToClean.length === 0) {
      console.log('Sweep: No orphaned or corrupt databases to clean up.');
      return;
    }

    console.log(`Sweep: Found ${pending.length} pending cleanup and ${corrupt.length} corrupt schema databases.`);

    // Get the list of databases that actually exist in IndexedDB
    let existingDbs: string[] = [];
    try {
      const databases = await indexedDB.databases();
      existingDbs = databases
        .filter(db => this.isSiQuoteDatabase(db))
        .map(db => db.name!.replace(`${this.DB_NAME_PREFIX}_`, ''));
    } catch {
      // indexedDB.databases() not supported — fall back to attempting deletion
      existingDbs = [...allToClean];
    }

    const deletionPromises = allToClean.map(async (projectGuid) => {
      // If the DB doesn't actually exist, just clean up the localStorage entries
      if (!existingDbs.includes(projectGuid)) {
        this.removePendingCleanup(projectGuid);
        this.removeCorruptSchema(projectGuid);
        console.log(`Sweep: Cleaned up localStorage for non-existent DB: ${projectGuid}`);
        return;
      }

      // DB still exists — try to delete it with aggressive timeouts for corrupt DBs
      try {
        const dbName = `${this.DB_NAME_PREFIX}_${projectGuid}`;
        const isCorrupt = corrupt.includes(projectGuid);
        const timeout = isCorrupt ? 2000 : 3000; // Shorter timeout for corrupt DBs
        
        const deleted = await new Promise<boolean>((resolve) => {
          const timeoutHandle = setTimeout(() => {
            console.warn(`Sweep: Delete timeout for ${projectGuid}`);
            resolve(false);
          }, timeout);

          const request = indexedDB.deleteDatabase(dbName);
          request.onsuccess = () => {
            clearTimeout(timeoutHandle);
            console.log(`Sweep: Successfully deleted ${dbName}`);
            resolve(true);
          };
          request.onerror = () => {
            clearTimeout(timeoutHandle);
            console.error(`Sweep: Delete error for ${projectGuid}:`, request.error);
            resolve(false);
          };
          request.onblocked = () => {
            console.warn(`Sweep: Delete blocked for ${projectGuid}. Another tab may be using it.`);
          };
        });
        
        if (deleted) {
          this.removePendingCleanup(projectGuid);
          this.removeCorruptSchema(projectGuid);
          console.log(`Sweep: Cleaned up successfully: ${projectGuid}`);
        } else {
          console.warn(`Sweep: Failed to delete ${projectGuid}. Will retry on next startup.`);
        }
      } catch (error) {
        console.error(`Sweep: Unexpected error cleaning up ${projectGuid}:`, error);
      }
    });

    await Promise.all(deletionPromises);
  }

  //#endregion
}
